import { Card } from 'antd';
import { useContext } from 'react';
import { PiAnchor, PiSignOutBold } from 'react-icons/pi';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '../config/cn';
import queryClient from '../config/query.ts';
import { AuthContext } from '../context/auth.context.tsx';
import { SidebarContext } from '../context/sidebar.context.tsx';
import { mainFeatures, resourcesMenu, superAdminSettings, MenuOption } from './menu-items';
import RenderMenu from './render-menu';

const SidebarBottom = () => {
	const navigate = useNavigate();
	const { isCollapsed } = useContext(SidebarContext);
	const { setUser, setIsAuthenticated, role, user } = useContext(AuthContext);

	// Filter menu items based on user's admin_type
	const filterMenuByRole = (menuItems: MenuOption[]): MenuOption[] => {
		if (!user?.admin_type) return [];

		return menuItems.filter((item) => item.roles.includes(user.admin_type!));
	};

	// Get filtered menu items based on admin_type
	const getFilteredMainFeatures = () => {
		return filterMenuByRole(mainFeatures);
	};

	const filteredSuperAdminSettings = filterMenuByRole(superAdminSettings);
	const filteredResourcesMenu = filterMenuByRole(resourcesMenu);

	// logout
	const handleLogout = () => {
		localStorage.removeItem('device_id');
		setIsAuthenticated(false);
		setUser(null);
		navigate('/login');
		queryClient.clear();
	};

	return (
		<div
			className={cn(
				'scrollbar-hidden dark:bg-black flex h-[calc(100dvh-4.6rem)] flex-col overflow-x-auto min-[1100px]:h-[calc(100dvh-6.1rem)] dark:text-white',
				{
					'w-24': isCollapsed,
					'w-72': !isCollapsed,
				},
			)}
		>
			<div className="px-5 pt-3">
				<Link
					to={role === 'admin' ? '/admin/dashboard' : '/retailer/dashboard'}
					title="Landing"
				>
					<div
						className={cn('flex items-center gap-2.5 rounded-lg px-2.5 py-2 transition-all duration-300', {
							'justify-center': isCollapsed,
							'bg-[#B18EED1A] text-link-active': location.pathname.includes('/dashboard'),
							'text-black-7 dark:text-white': !location.pathname.includes('/dashboard'),
						})}
					>
						<PiAnchor />
						{!isCollapsed && <div className={cn('truncate text-base font-semibold')}>Landing</div>}
					</div>
				</Link>

				<div className="pt-3" />

				{!isCollapsed && <div className="pb-2 pl-3 text-base font-bold text-gray-500">Management</div>}

				<RenderMenu items={getFilteredMainFeatures()} />
			</div>

			{filteredSuperAdminSettings.length > 0 && (
				<div className="px-5 py-3">
					{!isCollapsed && <div className="pb-2 pl-3 text-base font-bold text-gray-500">Settings</div>}

					<RenderMenu items={filteredSuperAdminSettings} />
				</div>
			)}

			{filteredResourcesMenu.length > 0 && (
				<div className="px-5 py-3">
					{!isCollapsed && <div className="pb-2 pl-3 text-base font-bold text-gray-500">Resources</div>}
					<RenderMenu items={filteredResourcesMenu} />
				</div>
			)}

			<div className="mt-auto px-5">
				<Card
					styles={{ body: { padding: 8 } }}
					className="bg-inherit"
				>
					<div
						className={cn('flex items-center justify-between text-sm', {
							hidden: isCollapsed,
						})}
					></div>

					<div
						className={cn('pt-3', {
							hidden: isCollapsed,
						})}
					/>
				</Card>

				<div className="pt-2" />

				<div
					className={cn('flex items-center gap-2.5 rounded-lg px-2.5 py-2 pb-6 transition-all duration-300', {
						'justify-center': isCollapsed,
					})}
					role="button"
					onClick={handleLogout}
					title="Logout"
				>
					<PiSignOutBold />
					{!isCollapsed && <div className={cn('truncate text-base font-semibold text-black-7 dark:text-white')}>Logout</div>}
				</div>
			</div>
		</div>
	);
};

export default SidebarBottom;
